import ErrorWithCause from '@sas/nova-core/error/ErrorWithCause';
import eventBus from '@sas/nova-core/eventBus';
import { getLogger } from '@sas/nova-core/logging';
import { isItem } from '@sas/nova-core/services/drive/util';
import { isContentQuerykey } from '../hooks/useContentQuery';
import { CONTENT_SERVICES_LOGGER_COMPONENT, createLogMsgFormatter } from '../util/__internal__/common';
import { contentQueryClient } from './__internal__/common';
import { getContentDataProvider } from './__internal__/ContentDataProvider';
export const CONTENT_MUTATION_EVENT = 'contentMutationEvent';
export const isContentMutationEventData = obj => {
  return Array.isArray(obj.items) && typeof obj.mutationType === 'string';
};
const logger = getLogger(CONTENT_SERVICES_LOGGER_COMPONENT);
const L = createLogMsgFormatter('/mutation');

/**
 * Notify the rest of the content components ecosystem that a mutation has occurred. This has two
 * main effects:
 * 1. Invalidate any ReactQuery queries that should be invalidated based on the mutation
 * 2. Send out an event on the eventBus which anything can listen to. The most critical use case for
 * this would be for cases such as deletes of items that are part of static queries, which may result
 * in content components/hooks/providers needing to update the query itself based on the mutation.
 * @param {object} config { items: array of Items or ItemIdentifiers, mutationType: one of 'create' 'delete' 'update', dataProvider: optional. Defaults to contentDataProvider. }
 */
export function mutationNotify(config) {
  const createCacheUpdater = (updateData, items) => cachedData => updateDataForCache?.(cachedData, items, updateData);
  try {
    const dataProvider = config.dataProvider ? config.dataProvider : getContentDataProvider();
    const queries = contentQueryClient.getQueryCache().getAll();
    for (const query of queries) {
      if (isContentQuerykey(query.queryKey[0])) {
        const queryKey = query.queryKey[0];

        // Enabled guard of useContentQuery should prevent this from ever happening, but just in case
        // and to make TS happy...
        if (!queryKey.queryTypeConfig) {
          continue;
        }

        // Handle special 'mixed' case
        if (queryKey.dataProviderInfo.type === 'mixed') {
          if (config.mutationType !== 'update' || queryKey.queryTypeConfig.type !== 'itemsStatic') {
            continue;
          }

          // The only mutation that fits these criteria currently (and hopefully ever) is
          // getMixedLocations. So, we should be safe to assume a 1-to-1 positional mapping of
          // queryData to queryKey.dataProviderInfo.value items, although care is taken to at least
          // not break if that assumption proves false

          // First, is there even an item id match
          const queryData = contentQueryClient.getQueryData(query.queryKey);
          if (!queryData) {
            continue;
          }
          let itemMatchIndex = -1;
          const itemMatch = config.items.some(item => {
            return queryData.some((queryItem, index) => {
              itemMatchIndex = index;
              if (!queryItem) {
                // It is valid for query data to have an undefined value - e.g. quarantined drive
                // is not available when requested for non-admins.
                return false;
              }
              if (isItem(item)) {
                return queryItem.resource.id === item.resource.id;
              } else {
                return dataProvider.doIdentifiersMatch(dataProvider.getResourceIdentifierFromItem(queryItem), item);
              }
            });
          });
          if (itemMatch && itemMatchIndex >= 0 && itemMatchIndex < queryKey.dataProviderInfo.value.length) {
            // If we get a match and a valid matchIndex, check if the mutation and the itemMatch
            // data provider use the same itemProvider
            const itemMatchItemProvider = queryKey.dataProviderInfo.value[itemMatchIndex].itemProvider;
            if (dataProvider.itemProvider === itemMatchItemProvider) {
              if (config.updateData && queryKey.queryId !== 'getFolderChildrenByIdentifier') {
                contentQueryClient.setQueryData(query.queryKey, createCacheUpdater(config.updateData, config.items));
              } else {
                // Item match and itemProvider match, so invalidate
                contentQueryClient.invalidateQueries(query.queryKey);
              }
            }
          }
        } else {
          // Mutations should never invalidate queries representing different itemProviders
          if (dataProvider.itemProvider !== queryKey.dataProviderInfo.value.itemProvider) {
            continue;
          }
          switch (config.mutationType) {
            case 'update':
              {
                switch (queryKey.queryTypeConfig.type) {
                  case 'itemStatic':
                  case 'itemsStatic':
                  case 'itemsContainer':
                    {
                      // For item updates, check if the query data contains the mutated items
                      const queryData = contentQueryClient.getQueryData(query.queryKey);
                      if (!queryData) {
                        continue;
                      }
                      const queryDataArray = Array.isArray(queryData) ? queryData : [queryData];
                      const itemMatch = config.items.some(item => {
                        return queryDataArray.some(queryItem => {
                          if (!queryItem) {
                            // It is valid for query data to have an undefined value - e.g. quarantined drive
                            // is not available when requested for non-admins.
                            return false;
                          }
                          if (isItem(item)) {
                            return queryItem.resource.id === item.resource.id;
                          } else {
                            return dataProvider.doIdentifiersMatch(dataProvider.getResourceIdentifierFromItem(queryItem), item);
                          }
                        });
                      });
                      if (itemMatch) {
                        if (config.updateData && queryKey.queryId !== 'getFolderChildrenByIdentifier') {
                          contentQueryClient.setQueryData(query.queryKey, createCacheUpdater(config.updateData, config.items));
                        } else {
                          contentQueryClient.invalidateQueries(query.queryKey);
                        }
                      }
                      break;
                    }
                  case 'ancestorsContainer':
                    {
                      // For ancestor updates, check if the ancestry path contains the mutated items
                      const queryData = contentQueryClient.getQueryData(query.queryKey);
                      if (!queryData) {
                        continue;
                      }
                      const ancestorMatch = config.items.some(item => {
                        return queryData.ancestors.some(ancestor => {
                          const ancestorIdentifier = dataProvider.getIdentifierFromAncestor(ancestor);
                          if (isItem(item)) {
                            return dataProvider.doesItemMatchIdentifier(item, ancestorIdentifier);
                          } else {
                            return dataProvider.doIdentifiersMatch(item, ancestorIdentifier);
                          }
                        });
                      });
                      if (ancestorMatch) {
                        contentQueryClient.invalidateQueries(query.queryKey);
                      }
                    }
                }
                break;
              }
            case 'create':
              {
                switch (queryKey.queryTypeConfig.type) {
                  case 'itemsContainer':
                    // Creates can only happen in an existing container. So, check if the container
                    // matches, and if so, invalidate
                    const containerIdentifier = queryKey.queryTypeConfig.containerIdentifier;
                    const itemMatch = config.items.some(item => {
                      if (isItem(item)) {
                        return dataProvider.doesItemMatchIdentifier(item, containerIdentifier);
                      } else {
                        return dataProvider.doIdentifiersMatch(item, containerIdentifier);
                      }
                    });
                    if (itemMatch) {
                      contentQueryClient.invalidateQueries(query.queryKey);
                    }
                    break;
                }
                break;
              }
            case 'delete':
              {
                switch (queryKey.queryTypeConfig.type) {
                  case 'itemsContainer':
                    // For deletes, items deleted from a container are the only things can should be
                    // handled by invalidating the query. Everything else breaks actual queries and so
                    // must be handled elsewhere by listening for the eventBus event
                    const queryData = contentQueryClient.getQueryData(query.queryKey);
                    if (!queryData) {
                      continue;
                    }
                    const itemMatch = config.items.some(item => {
                      return queryData.some(queryItem => {
                        if (!queryItem) {
                          // It is valid for query data to have an undefined value - e.g. quarantined drive
                          // is not available when requested for non-admins.
                          return false;
                        }
                        if (isItem(item)) {
                          return queryItem.resource.id === item.resource.id;
                        } else {
                          return dataProvider.doIdentifiersMatch(dataProvider.getResourceIdentifierFromItem(queryItem), item);
                        }
                      });
                    });
                    if (itemMatch) {
                      contentQueryClient.invalidateQueries(query.queryKey);
                    }
                    break;
                }
                break;
              }
            case 'removeFrom':
              {
                switch (queryKey.queryTypeConfig.type) {
                  case 'itemsContainer':
                    const containerIdentifier = queryKey.queryTypeConfig.containerIdentifier;
                    const itemMatch = config.items.some(item => {
                      if (isItem(item)) {
                        return dataProvider.doesItemMatchIdentifier(item, containerIdentifier);
                      } else {
                        return dataProvider.doIdentifiersMatch(item, containerIdentifier);
                      }
                    });
                    if (itemMatch) {
                      contentQueryClient.invalidateQueries(query.queryKey);
                    }
                    break;
                }
                break;
              }
          }
        }
      }
    }
    eventBus.publish(CONTENT_MUTATION_EVENT, {
      data: config
    });
  } catch (e) {
    const error = new MutationError(L(['Unexpected error in mutationNotify', ['error', e]]), {
      cause: e
    });
    logger.debug(error.message);
    throw error;
  }
}
class MutationError extends ErrorWithCause {
  constructor(...args) {
    super(...args);
    this.name = 'MutationError';
  }
}
function updateDataForCache(cachedData, items, updater) {
  if (!cachedData) return cachedData;
  if (Array.isArray(cachedData)) {
    return cachedData.map(cachedRQItem => {
      const matchingItem = items?.find(item => item.resource.id === cachedRQItem.resource.id);
      if (matchingItem) {
        return newObjForCache(cachedRQItem, updater);
      }
      return cachedRQItem;
    });
  } else {
    const matchingItem = items?.find(item => item.resource.id === cachedData.resource.id);
    if (matchingItem) {
      return newObjForCache(cachedData, updater);
    }
    return cachedData;
  }
}
export function newObjForCache(obj, updater) {
  const objToCache = structuredClone(obj);
  // do caller-scoped updates
  updater(objToCache);
  if (objToCache && !objToCache._updated) {
    // temp mark the obj to ensure rerender is triggered.
    objToCache._updated = true;
    setTimeout(() => {
      delete objToCache._updated;
    }, 0);
  }
  return objToCache;
}
//# sourceMappingURL=mutation.js.map